import { Controller } from '@hotwired/stimulus'

const sleep = duration => new Promise(resolve => setTimeout(resolve, duration))

// Staggered sessions in the same section, in document order. A session can
// only start after every session before it has, `stagger` ms apart, so they
// always play left to right and top to bottom.
const staggerGroups = new Map()

// Matches the CLI's brand animation: 28 frames, 22ms apart.
const sweepFrames = 28
const sweepInterval = 22

// Plays a server-rendered terminal session back in document order: steps
// appear, typed text is typed out, output lines print one at a time, cells
// sweep in, and frames (prompts that clear once answered) show and then
// hide. A `replay` target shows once the session finishes and plays it again.
// Without JS or with reduced motion the finished session simply shows.
export default class extends Controller {
  static targets = ['cell', 'frame', 'line', 'replay', 'step', 'typed']
  static values = {
    autoplay: { type: Boolean, default: false },
    charDelay: { type: Number, default: 22 },
    // Removes pending lines from layout and keeps the newest one in view,
    // like a terminal, instead of reserving the finished height.
    follow: { type: Boolean, default: false },
    lineDelay: { type: Number, default: 70 },
    runDelay: { type: Number, default: 900 },
    // Minimum gap from the previous staggered session's start.
    stagger: { type: Number, default: 0 },
    stepDelay: { type: Number, default: 350 }
  }

  connect() {
    const reducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches

    if (reducedMotion) return

    this.#prepare()

    if (this.autoplayValue) {
      this.#run()

      return
    }

    if (this.staggerValue) this.#joinStaggerGroup()

    this.observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return

        this.observer.disconnect()

        if (this.staggerValue) {
          this.#startInOrder()
        } else {
          this.#run()
        }
      },
      { threshold: 0.35 }
    )

    this.observer.observe(this.element)
  }

  disconnect() {
    this.observer?.disconnect()
    this.stopped = true

    const sessions = this.staggerGroup?.sessions
    if (sessions) sessions.splice(sessions.indexOf(this), 1)
  }

  async replay() {
    this.replayTarget.hidden = true
    this.element.style.opacity = 0
    await sleep(500)
    this.#reset(this.element)
    this.element.scrollTop = 0
    this.element.style.opacity = ''
    this.#run()
  }

  #joinStaggerGroup() {
    const key = this.element.closest('section') ?? document.body
    const group = staggerGroups.get(key) ?? { sessions: [], nextStart: 0 }

    group.sessions.push(this)
    group.sessions.sort((a, b) =>
      a.element.compareDocumentPosition(b.element) &
      Node.DOCUMENT_POSITION_FOLLOWING
        ? -1
        : 1
    )
    staggerGroups.set(key, group)
    this.staggerGroup = group
  }

  // Starts this session and any earlier ones that haven't started yet.
  #startInOrder() {
    const { sessions } = this.staggerGroup

    sessions.slice(0, sessions.indexOf(this) + 1).forEach(session => {
      session.#queueStart()
    })
  }

  async #queueStart() {
    if (this.queued) return

    this.queued = true
    this.observer.disconnect()

    const group = this.staggerGroup
    const now = performance.now()
    const wait = Math.max(0, group.nextStart - now)

    group.nextStart = now + wait + this.staggerValue
    await sleep(wait)
    if (!this.stopped) this.#run()
  }

  #prepare() {
    if (!this.followValue) {
      this.element.style.minHeight = `${this.element.offsetHeight}px`
    }

    this.typedTargets.forEach(typed => {
      typed.dataset.text = typed.textContent.trim()
    })
    this.#reset(this.element)
  }

  async #run() {
    await this.#play(this.element)
    if (this.stopped || !this.hasReplayTarget) return

    this.replayTarget.hidden = false

    if (this.followValue) {
      this.element.scrollTo({
        behavior: 'smooth',
        top: this.element.scrollHeight
      })
    }
  }

  #within(root, targets) {
    return targets.filter(target => root.contains(target))
  }

  #sequence(root) {
    const targets = this.#within(root, [
      ...this.stepTargets,
      ...this.typedTargets,
      ...this.lineTargets,
      ...this.cellTargets,
      ...this.frameTargets
    ])

    return targets.sort((a, b) =>
      a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1
    )
  }

  #reset(root) {
    this.#within(root, this.stepTargets).forEach(step => this.#hide(step))
    this.#within(root, this.lineTargets).forEach(line => this.#hide(line))
    this.#within(root, this.cellTargets).forEach(cell => {
      cell.style.visibility = 'hidden'
    })
    this.#within(root, this.typedTargets).forEach(typed => {
      typed.textContent = ''
    })
  }

  async #play(root) {
    let previous = null
    let swept = false

    for (const target of this.#sequence(root)) {
      if (this.stopped) return

      if (this.cellTargets.includes(target)) {
        if (!swept) {
          swept = true
          await this.#sweep(root)
        }
      } else if (this.frameTargets.includes(target)) {
        await this.#flash(target)
      } else if (this.stepTargets.includes(target)) {
        if (previous) await sleep(this.#delayBefore(target, previous))
        this.#show(target)
      } else if (this.typedTargets.includes(target)) {
        await this.#type(target)
      } else {
        await sleep(this.#delayBefore(target, previous))
        this.#show(target)
      }

      previous = target
    }
  }

  // A `data-delay` on the step or line overrides the default pause.
  #delayBefore(target, previous) {
    if (target.dataset.delay) return Number(target.dataset.delay)
    if (this.stepTargets.includes(target)) return this.stepDelayValue

    const afterCommand = previous && this.typedTargets.includes(previous)

    return afterCommand ? this.runDelayValue : this.lineDelayValue
  }

  // The untyped rest of the text stays in the layout, transparent, so every
  // line breaks where it will when finished (`text-pretty` balances breaks
  // across the whole paragraph) and nothing jumps lines while typing.
  async #type(element) {
    const text = element.dataset.text
    const delay = this.charDelayValue / (Number(element.dataset.speed) || 1)
    const typed = document.createTextNode('')
    const rest = document.createElement('span')

    rest.dataset.rest = ''
    element.replaceChildren(typed, rest)

    for (let index = 1; index <= text.length; index += 1) {
      if (this.stopped) return

      typed.data = text.slice(0, index)
      rest.textContent = text.slice(index)
      this.#scroll()
      await sleep(delay)
    }

    element.textContent = text
  }

  async #sweep(root) {
    const cells = this.#within(root, this.cellTargets).sort(
      (a, b) => Number(a.dataset.order) - Number(b.dataset.order)
    )
    const batch = Math.max(1, Math.floor(cells.length / sweepFrames))

    for (let index = 0; index < cells.length; index += batch) {
      if (this.stopped) return

      cells.slice(index, index + batch).forEach(cell => {
        cell.style.visibility = ''
      })
      await sleep(sweepInterval)
    }
  }

  async #flash(frame) {
    await sleep(this.stepDelayValue)
    frame.hidden = false
    this.#scroll()
    await sleep(Number(frame.dataset.hold) || 1500)
    frame.hidden = true
  }

  #hide(element) {
    if (this.followValue) {
      element.hidden = true
    } else {
      element.style.visibility = 'hidden'
    }
  }

  #show(element) {
    element.hidden = false
    element.style.visibility = ''
    this.#scroll()
  }

  #scroll() {
    if (this.followValue) this.element.scrollTop = this.element.scrollHeight
  }
}
